import {
  Box,
  Card,
  Grid,
  pn,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { Input, Select } from "components";
import { StudentBulkAssignGroup, StudentBulkAssignGroupResource, StudentGroup } from "generated/openApi";
import produce from "immer";

export const StudentGroupInfo = (props: {
  bulkAssignGroup: StudentBulkAssignGroup;
  setBulkAssignGroup: (value: StudentBulkAssignGroup) => void;
  resource: StudentBulkAssignGroupResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const properties = props.resource.properties || [];

  const studentGroupProperties =
    properties.find((x) => x.propertyName === pn<StudentBulkAssignGroup>("createStudentGroup"))?.children || [];

  const studentGroupValidationErrors = props.validationErrors[pn<StudentBulkAssignGroup>("createStudentGroup")] || [];

  const nameField = () => {
    const propertyName = pn<StudentGroup>("name");
    const property = studentGroupProperties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        
        modelProperty={property}
        errors={studentGroupValidationErrors[propertyName]}
        value={props.bulkAssignGroup.createStudentGroup?.name}
        onChange={(e) =>
          props.setBulkAssignGroup(
            produce(props.bulkAssignGroup, (draft) => {
              draft.createStudentGroup!.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentGroup>("schoolId");
    const property = studentGroupProperties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={studentGroupValidationErrors[propertyName]}
        value={props.resource.schools?.find((x) => x.id === props.bulkAssignGroup.createStudentGroup?.schoolId)}
        options={props.resource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setBulkAssignGroup(
            produce(props.bulkAssignGroup, (draft) => {
              draft.createStudentGroup!.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const renderNewStudentGroup = () => {
    return (
      <Grid container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {nameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {schoolField()}
        </Grid>
      </Grid>
    );
  };

  const renderExistingStudentGroup = () => {
    const propertyName = pn<StudentBulkAssignGroup>("existingGroupId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        required
        value={props.resource.studentGroups?.find((x) => x.id === props.bulkAssignGroup.existingGroupId)}
        options={props.resource.studentGroups}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setBulkAssignGroup(
            produce(props.bulkAssignGroup, (draft) => {
              draft.existingGroupId = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Card header="Group">
      <Stack ref={breakpointsRef} spacing="md">
        <Stack spacing="sm">
          <Typography>Assign students to:</Typography>
          <ToggleButtonGroup
            value={props.bulkAssignGroup.isCreateNewGroup}
            exclusive
            onChange={(event: React.MouseEvent<HTMLElement>, value: boolean) => {
              props.setBulkAssignGroup(
                produce(props.bulkAssignGroup, (draft) => {
                  draft.isCreateNewGroup = value;
                })
              );
            }}
          >
            <ToggleButton key={2} value={true}>
              + New group
            </ToggleButton>
            <ToggleButton key={1} value={false}>
              Existing group
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {props.bulkAssignGroup.isCreateNewGroup ? (
          <Box>{renderNewStudentGroup()}</Box>
        ) : (
          <Box>{renderExistingStudentGroup()}</Box>
        )}
      </Stack>
    </Card>
  );
};
